// Scene Editor Scene
import { Scene } from '@/engine/scene/Scene';
import { logger } from '@/engine/logging';
import { eventBus } from '@/engine/events/EventBus';
import { computeLayout, readSafeInsets } from '@/engine/render/LayoutEngine';
import { RENDER_CONFIG } from '@/engine/render/RenderConfig';
import { sceneState, editorMode, setEditorMode } from '../stores/sceneEditorStore';
import type { SceneObject, SceneEditorMode } from '../types';
import * as PIXI from 'pixi.js';
import { CoordinateService } from '@/engine';
import { MouseInteractionSystem, GizmoSystem, SceneObjectSystem, GridSystem } from '../systems';
import type { PixiSpriteRenderer } from '@/engine/components/PixiSpriteRenderer';

type Deps = { stage: PIXI.Container; game: any; audio: any };

export class SceneEditorScene extends Scene {
  private stage?: PIXI.Container;
  private bgContainer?: PIXI.Container;
  private contentContainer?: PIXI.Container;
  private viewport: PIXI.Container;
  private bgWidth = RENDER_CONFIG.referenceResolution.w;
  private bgHeight = RENDER_CONFIG.referenceResolution.h;

  // Systems
  private mouseInteractionSystem?: MouseInteractionSystem;
  private gizmoSystem?: GizmoSystem;
  private sceneObjectSystem?: SceneObjectSystem;
  private gridSystem?: GridSystem;

  // Subscriptions
  private unsubscribeState: (() => void) | null = null;
  private unsubscribeMode: (() => void) | null = null;
  private keyboardCleanup: (() => void) | null = null;

  // важно: не .bind() на (un)subscribe
  private readonly onResize = () => {
    this.relayout();
    this.notifyResize();
  };

  constructor() {
    super();
    this.viewport = new PIXI.Container();
  }

  async onEnter(previousScene: Scene | null) {
    super.onEnter(previousScene);
    logger.info('🎬 SceneEditorScene entered');

    const { stage } = this.requireManagers();

    // менеджеры/контейнеры
    this.stage = stage;
    this.initContainers();
    this.initializeSystems();
    this.subscribeToState();
    this.subscribeToMode();
    this.hookResize();
    this.setupKeyboardInteraction();
    this.relayout();

    logger.info('✅ SceneEditorScene initialized', {
      source: 'scene-editor',
      stage: !!this.stage,
      bgContainer: !!this.bgContainer,
      contentContainer: !!this.contentContainer,
      viewport: !!this.viewport,
      systems: {
        mouse: !!this.mouseInteractionSystem,
        gizmo: !!this.gizmoSystem,
        objects: !!this.sceneObjectSystem,
        grid: !!this.gridSystem,
      },
    });
  }

  onExit(nextScene: Scene | null) {
    super.onExit(nextScene);
    logger.info('🎬 SceneEditorScene exited');

    this.unsubscribeState?.();
    this.unsubscribeState = null;
    this.unsubscribeMode?.();
    this.unsubscribeMode = null;
    this.keyboardCleanup?.();
    this.keyboardCleanup = null;
    eventBus.off('window-resize', this.onResize);

    // Останавливаем все системы через Scene API
    this.stopAllSystems();

    // Очищаем системы
    this.mouseInteractionSystem = undefined;
    this.gizmoSystem = undefined;
    this.sceneObjectSystem = undefined;
    this.gridSystem = undefined;

    // контейнеры отпустим; Pixi сцена их зачистит по своему жизненному циклу
    this.bgContainer = undefined;
    this.contentContainer = undefined;
    this.stage = undefined;
  }

  // update(dt: number): void { super.update(dt); }

  // Resize обрабатывается через eventBus в main.ts
  // Здесь только обновляем внутренние компоненты
  // ——— Публичные методы ———

  /** Добавить объект в сцену */
  async addSceneObject(object: SceneObject) {
    return this.sceneObjectSystem?.addSceneObject(object);
  }

  /** Удалить объект из сцены */
  removeSceneObject(objectId: string) {
    this.sceneObjectSystem?.removeSceneObject(objectId);
  }

  /** Обновить объект в сцене */
  async updateSceneObject(object: SceneObject) {
    return this.sceneObjectSystem?.updateSceneObject(object);
  }

  /** Получить объект по ID */
  getSceneObject(objectId: string): PixiSpriteRenderer | null {
    return this.sceneObjectSystem?.getSceneObject(objectId) || null;
  }

  // ——— Приватные методы ———

  /** Инициализация систем */
  private initializeSystems() {
    // Создаем системы
    this.sceneObjectSystem = new SceneObjectSystem(this);
    this.gizmoSystem = new GizmoSystem(this);
    this.mouseInteractionSystem = new MouseInteractionSystem(this);
    this.gridSystem = new GridSystem(this);

    // Инициализируем системы
    this.sceneObjectSystem.initialize(
      this.viewport,
      this.bgContainer!,
      this.bgWidth,
      this.bgHeight
    );
    this.gizmoSystem.initialize(this.viewport, this.stage!); // Передаем stage для глобальных событий
    this.mouseInteractionSystem.initialize(
      this.viewport,
      this.gizmoSystem.getGizmoContainer()!,
      this.sceneObjectSystem.findObjectAtPosition.bind(this.sceneObjectSystem)
    );
    this.gridSystem.initialize(this.bgContainer!, this.bgWidth, this.bgHeight);

    // Регистрируем системы через Scene API
    this.useSystem(this.sceneObjectSystem, {
      name: 'SceneObjectSystem',
      phase: 'main',
      autostart: true,
      priority: 10,
    });

    this.useSystem(this.gizmoSystem, {
      name: 'GizmoSystem',
      phase: 'main',
      autostart: true,
      priority: 5,
    });

    this.useSystem(this.mouseInteractionSystem, {
      name: 'MouseInteractionSystem',
      phase: 'main',
      autostart: true,
      priority: 1,
    });

    this.useSystem(this.gridSystem, {
      name: 'GridSystem',
      phase: 'main',
      autostart: true,
      priority: 0,
    });

    logger.info('✅ Systems registered and started', {
      source: 'scene-editor',
      systems: {
        sceneObject: this.sceneObjectSystem.started,
        gizmo: this.gizmoSystem.started,
        mouse: this.mouseInteractionSystem.started,
        grid: this.gridSystem.started,
      },
    });
  }

  private requireManagers(): Deps {
    if (!this.managers) {
      const err = new Error('Managers not provided');
      logger.error('❌ Менеджеры не переданы в сцену!', err);
      throw err;
    }
    const { stage, game, audio } = this.managers as Deps;
    return { stage, game, audio };
  }

  private hookResize() {
    eventBus.on('window-resize', this.onResize);
  }

  private initContainers() {
    if (!this.stage) return;

    this.bgContainer = new PIXI.Container();
    this.contentContainer = new PIXI.Container();

    // Включаем сортировку по zIndex для правильного отображения слоев
    this.stage.sortableChildren = true;
    this.contentContainer.sortableChildren = true;
    this.viewport.sortableChildren = true;

    // ВАЖНО: Настраиваем viewport для интерактивности (PIXI 8)
    this.viewport.eventMode = 'static';
    this.viewport.interactiveChildren = true;

    this.stage.addChild(this.bgContainer, this.contentContainer);
    this.contentContainer.addChild(this.viewport);

    logger.info('📦 Containers initialized', {
      source: 'scene-editor',
      stage: !!this.stage,
      bgContainer: !!this.bgContainer,
      contentContainer: !!this.contentContainer,
      viewport: !!this.viewport,
      viewportEventMode: this.viewport.eventMode,
      stageChildren: this.stage.children.length,
      contentContainerChildren: this.contentContainer.children.length,
      viewportChildren: this.viewport.children.length,
    });
  }

  private subscribeToState() {
    if (this.unsubscribeState) return;
    this.unsubscribeState = sceneState.subscribe(async state => {
      logger.info('📡 State changed', {
        source: 'scene-editor',
        objectsCount: Object.keys(state.objects).length,
        selectedId: state.selectedObjectId,
        viewport: state.viewport,
        viewportPosition: { x: this.viewport.position.x, y: this.viewport.position.y },
        viewportScale: { x: this.viewport.scale.x, y: this.viewport.scale.y },
      });

      this.viewport.position.set(state.viewport.x, state.viewport.y);
      this.viewport.scale.set(state.viewport.zoom);

      // Синхронизируем объекты через систему
      await this.syncSceneObjects(state.objects);

      // Обновляем gizmo для выделенного объекта
      this.gizmoSystem?.updateGizmoForSelectedObject();
    });
  }

  private async syncSceneObjects(objects: Record<string, SceneObject>) {
    const currentIds = new Set(Object.keys(objects));
    const sceneIds = new Set(this.sceneObjectSystem?.sceneObjectsMap?.keys() || []);

    logger.info('🔄 Syncing scene objects', {
      source: 'scene-editor',
      currentIds: Array.from(currentIds),
      sceneIds: Array.from(sceneIds),
      objectsCount: Object.keys(objects).length,
      sceneObjectsCount: this.sceneObjectSystem?.sceneObjectsMap?.size || 0,
      objects: Object.values(objects).map(obj => ({ id: obj.id, type: obj.type, name: obj.name })),
    });

    // Удаляем объекты, которых нет в состоянии
    sceneIds.forEach(id => {
      if (!currentIds.has(id)) {
        logger.info('🗑️ Removing object', { source: 'scene-editor', id });
        this.sceneObjectSystem?.removeSceneObject(id);
      }
    });

    // Добавляем/обновляем объекты
    for (const object of Object.values(objects)) {
      if (sceneIds.has(object.id)) {
        logger.info('🔄 Updating object', {
          source: 'scene-editor',
          id: object.id,
          type: object.type,
          name: object.name,
          position:
            object.type === 'sprite' ? { x: (object as any).x, y: (object as any).y } : null,
        });
        await this.sceneObjectSystem?.updateSceneObject(object);
      } else {
        logger.info('➕ Adding object', {
          source: 'scene-editor',
          id: object.id,
          type: object.type,
          name: object.name,
          position:
            object.type === 'sprite' ? { x: (object as any).x, y: (object as any).y } : null,
        });
        await this.sceneObjectSystem?.addSceneObject(object);
      }
    }

    logger.info('🔄 Sync completed', {
      source: 'scene-editor',
      finalSceneObjectsCount: this.sceneObjectSystem?.sceneObjectsMap?.size || 0,
      viewportChildren: this.viewport.children.length,
    });
  }

  private relayout() {
    if (!this.bgContainer || !this.contentContainer) return;

    const insets = readSafeInsets();
    const layout = computeLayout(window.innerWidth, window.innerHeight, insets);

    // sync сервис координат
    CoordinateService.getInstance().setLayout(layout);

    // background
    this.bgContainer.x = layout.bg.x;
    this.bgContainer.y = layout.bg.y;
    this.bgContainer.hitArea = new PIXI.Rectangle(0, 0, layout.bg.w, layout.bg.h);
    this.sceneObjectSystem?.resizeBackground(layout.bg.w, layout.bg.h);

    // game
    this.contentContainer.x = layout.game.x;
    this.contentContainer.y = layout.game.y;
    this.contentContainer.scale.set(layout.scaleGame);
    this.contentContainer.hitArea = new PIXI.Rectangle(0, 0, layout.game.w, layout.game.h);

    // Настраиваем viewport для scene editor
    this.viewport.hitArea = new PIXI.Rectangle(0, 0, layout.game.w, layout.game.h);

    // Update grid size through system
    this.gridSystem?.resize(layout.bg.w, layout.bg.h);
  }

  private subscribeToMode() {
    if (this.unsubscribeMode) return;
    this.unsubscribeMode = editorMode.subscribe(mode => {
      this.updateCursor();

      // Обновляем режим gizmo
      this.gizmoSystem?.updateGizmoMode(mode);
    });
  }

  private setupKeyboardInteraction() {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space' && !event.repeat) {
        // Temporarily switch to pan mode
        setEditorMode('pan');
        event.preventDefault();
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        // Return to previous mode (select by default)
        setEditorMode('select');
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Store cleanup function
    this.keyboardCleanup = () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }

  private getCursorForMode(mode: SceneEditorMode): string {
    switch (mode) {
      case 'select':
        return 'default';
      case 'move':
        return 'move';
      case 'scale':
        return 'nw-resize';
      case 'rotate':
        return 'grab';
      case 'pan':
        return 'grab';
      default:
        return 'default';
    }
  }

  private updateCursor() {
    if (!this.viewport) return;
    const currentMode = editorMode.get();
    this.viewport.cursor = this.getCursorForMode(currentMode);
  }
}
